import { jsxs, jsx } from "react/jsx-runtime";
import { Conversation, ConversationContent, ConversationScrollButton } from "../../ai-elements/conversation.js";
import { memo, useMemo, useCallback } from "react";
import { useConversationScrollDetection } from "../hooks/useConversationScrollDetection.js";
import ConciergeWelcome from "./ConciergeWelcome.js";
import { ConversationSkeleton } from "./ConversationSkeleton.js";
import { IconHome } from "./ConciergeIcons.js";
import { MessageListV2 } from "./MessageListV2.js";
import { PromptInputConcierge } from "./PromptInputConcierge.js";
import { ConciergeTrendingSearches } from "./ConciergeTrendingSearches.js";
import { useSuggestedQueries } from "../../api/cache/appConfigQueryOptions.js";
const ConciergeInputArea = ({
  searchInputValue,
  setSearchInputValue,
  handleSearch,
  isActivelyStreaming,
  conversationHistoryLength,
  onTrackAccessPoint
}) => {
  const placeholder = useMemo(
    () => conversationHistoryLength > 0 ? "Ask a follow up question" : "How can I help you today?",
    [conversationHistoryLength]
  );
  const handleSearchWithTracking = useCallback(
    (text, imageData, imageMimeType) => {
      onTrackAccessPoint == null ? void 0 : onTrackAccessPoint("search_bar", text);
      handleSearch(text, imageData, imageMimeType);
    },
    [handleSearch, onTrackAccessPoint]
  );
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: "tw:flex tw:shrink-0 tw:flex-col tw:gap-4 tw:px-4 tw:pt-4",
      style: { paddingBottom: "max(1rem, env(safe-area-inset-bottom))" },
      children: /* @__PURE__ */ jsx(
        PromptInputConcierge,
        {
          placeholder,
          value: searchInputValue,
          onChange: setSearchInputValue,
          onSubmit: handleSearchWithTracking,
          isStreaming: isActivelyStreaming
        }
      )
    }
  );
};
const ConciergeChatAreaV2Component = ({
  appName: _appName,
  brandId,
  conversationId,
  isEmbedded = false,
  apiBaseUrl,
  userFirstName,
  pageContext,
  conversationHistory,
  productMetadataMap,
  surveyMetadataMap,
  streamingProductGroups,
  previewSearchQueries,
  previewQueryProducts,
  followUpQuestions,
  streamingFeaturedProduct,
  streamingStoreLocator,
  streamingLoginRequired,
  streamingDevInfo,
  onSignIn,
  isActivelyStreaming,
  streamingAIResponse,
  currentStatus,
  isWaitingForFollowUpQueries,
  isLoadingConversationQuery,
  isPendingAssistantResponse,
  isPollingTimedOut,
  onRetry,
  handleSearch,
  onUpdateFeedback,
  onVariantSelect,
  onTrackAccessPoint,
  onHomeClick,
  isSidebarExpanded = true,
  brandLogoUrl,
  searchInputValue,
  setSearchInputValue,
  isSharedView,
  sharedByUserName,
  sourceConversationId,
  sourceCheckpointIndex
}) => {
  const showWelcome = conversationHistory.length === 0 && !isLoadingConversationQuery;
  const { data: suggestedQueriesData } = useSuggestedQueries(brandId ? parseInt(brandId) : void 0, apiBaseUrl);
  const suggestedQueries = useMemo(() => (suggestedQueriesData == null ? void 0 : suggestedQueriesData.suggestedQuestions) || [], [suggestedQueriesData]);
  const hasImageCarousel = !isEmbedded;
  const showTypingIndicator = isActivelyStreaming && !streamingAIResponse && previewSearchQueries.length === 0 && previewQueryProducts.length === 0;
  const scrollDetectionParams = useMemo(
    () => ({
      followUpQuestions,
      isActivelyStreaming,
      conversationHistoryLength: conversationHistory.length,
      showWelcome,
      searchInputValue: ""
      // Not actually used in the hook, but required by interface
    }),
    [followUpQuestions, isActivelyStreaming, conversationHistory.length, showWelcome]
  );
  const { bottomRef } = useConversationScrollDetection(scrollDetectionParams);
  const stableHandleSearch = useMemo(() => handleSearch, [handleSearch]);
  const handleWelcomeSearch = useCallback(
    (searchTerm) => {
      onTrackAccessPoint == null ? void 0 : onTrackAccessPoint("quick_prompts", searchTerm);
      stableHandleSearch(searchTerm);
    },
    [stableHandleSearch, onTrackAccessPoint]
  );
  const handleFollowUpSearch = useCallback(
    (searchTerm) => {
      onTrackAccessPoint == null ? void 0 : onTrackAccessPoint("follow_up_question", searchTerm);
      stableHandleSearch(searchTerm);
    },
    [stableHandleSearch, onTrackAccessPoint]
  );
  const welcomeProps = useMemo(
    () => ({
      onSearch: handleWelcomeSearch,
      brandId: brandId ? parseInt(brandId) : void 0,
      isEmbedded,
      apiBaseUrl,
      userFirstName,
      pageContext
    }),
    [handleWelcomeSearch, brandId, isEmbedded, apiBaseUrl, userFirstName, pageContext]
  );
  const SimpleWelcomeHeader = useMemo(
    () => /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:flex-col tw:items-center tw:justify-center tw:w-full tw:text-center", children: [
      /* @__PURE__ */ jsx("p", { className: "tw:font-bold tw:text-[16px] tw:leading-[32px] tw:tracking-[2px] tw:uppercase tw:text-primary", children: "CONCIERGE" }),
      /* @__PURE__ */ jsxs("h1", { className: "tw:font-semibold tw:text-[24px] tw:leading-[32px] tw:tracking-[0.08px] tw:mt-4 tw:text-primary", children: [
        "Let us help you find what you've been looking for",
        userFirstName ? `, ${userFirstName}` : "",
        "."
      ] })
    ] }),
    [userFirstName]
  );
  const conversationContent = useMemo(() => {
    if (isLoadingConversationQuery) {
      return /* @__PURE__ */ jsx(ConversationSkeleton, {});
    }
    if (showWelcome) {
      if (hasImageCarousel) {
        return SimpleWelcomeHeader;
      }
      return /* @__PURE__ */ jsx(ConciergeWelcome, { ...welcomeProps });
    }
    return /* @__PURE__ */ jsx(
      MessageListV2,
      {
        conversationId,
        messages: conversationHistory,
        showTypingIndicator,
        streamingAIResponse,
        productMetadataMap,
        surveyMetadataMap,
        streamingProductGroups,
        currentStatus,
        previewSearchQueries,
        previewQueryProducts,
        isWaitingForFollowUpQueries,
        isActivelyStreaming,
        streamingFeaturedProduct,
        streamingStoreLocator,
        streamingLoginRequired,
        streamingDevInfo,
        onSignIn,
        followUpQuestions,
        onSearch: handleFollowUpSearch,
        onUpdateFeedback,
        onVariantSelect,
        bottomRef,
        apiBaseUrl,
        isPendingAssistantResponse,
        isPollingTimedOut,
        onRetry,
        isSharedView,
        sharedByUserName,
        sourceConversationId,
        sourceCheckpointIndex
      }
    );
  }, [
    isLoadingConversationQuery,
    showWelcome,
    hasImageCarousel,
    SimpleWelcomeHeader,
    welcomeProps,
    conversationId,
    conversationHistory,
    showTypingIndicator,
    streamingAIResponse,
    productMetadataMap,
    streamingProductGroups,
    currentStatus,
    previewSearchQueries,
    previewQueryProducts,
    isWaitingForFollowUpQueries,
    isActivelyStreaming,
    streamingFeaturedProduct,
    streamingStoreLocator,
    streamingLoginRequired,
    streamingDevInfo,
    onSignIn,
    followUpQuestions,
    handleFollowUpSearch,
    onUpdateFeedback,
    onVariantSelect,
    bottomRef,
    isPendingAssistantResponse,
    isPollingTimedOut,
    onRetry,
    isSharedView,
    sharedByUserName,
    sourceConversationId,
    sourceCheckpointIndex
  ]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "tw:relative tw:flex tw:flex-1 tw:flex-col",
      style: {
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden"
      },
      children: [
        onHomeClick && !isSidebarExpanded && /* @__PURE__ */ jsx("div", { className: "tw:flex tw:items-center tw:shrink-0 tw:px-4 tw:py-3", style: { minHeight: 48 }, children: brandLogoUrl ? /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: onHomeClick,
            className: "tw:flex tw:items-center tw:rounded tw:p-1 tw:transition-colors hover:tw:bg-black/5",
            "aria-label": "Go to home",
            children: /* @__PURE__ */ jsx("img", { src: brandLogoUrl, alt: "Brand", className: "tw:h-10 tw:w-auto tw:max-w-[140px] tw:object-contain" })
          }
        ) : /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            onClick: onHomeClick,
            className: "tw:flex tw:items-center tw:gap-2 tw:rounded tw:px-2 tw:py-1.5 tw:text-sm tw:font-medium tw:text-primary hover:tw:bg-black/5 tw:transition-colors",
            "aria-label": "Go to home",
            children: [
              /* @__PURE__ */ jsx(IconHome, { size: 20 }),
              /* @__PURE__ */ jsx("span", { children: "Home" })
            ]
          }
        ) }),
        /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              flex: 1,
              overflow: showWelcome ? "auto" : "auto",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              maxHeight: "100%"
            },
            children: showWelcome ? (
              // Welcome screen layout - different based on whether we have image carousel
              hasImageCarousel ? (
                // Image carousel layout: header -> input -> carousel
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      position: "relative",
                      overflow: "auto"
                    },
                    children: [
                      /* @__PURE__ */ jsx("div", { style: { height: "120px", flexShrink: 0 } }),
                      conversationContent,
                      /* @__PURE__ */ jsx("div", { className: "tw:mt-8 tw:w-full tw:max-w-[768px] tw:px-4 md:tw:px-0", children: /* @__PURE__ */ jsx(
                        PromptInputConcierge,
                        {
                          placeholder: "Ask anything",
                          value: searchInputValue,
                          onChange: setSearchInputValue,
                          onSubmit: handleSearch,
                          isStreaming: isActivelyStreaming
                        }
                      ) }),
                      /* @__PURE__ */ jsx(ConciergeTrendingSearches, { suggestedQueries, onSearch: handleSearch }),
                      /* @__PURE__ */ jsx("div", { style: { height: "40px", flexShrink: 0 } })
                    ]
                  }
                )
              ) : (
                // Simple centered layout for non-image mode
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: {
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    },
                    children: conversationContent
                  }
                )
              )
            ) : /* @__PURE__ */ jsxs(Conversation, { children: [
              /* @__PURE__ */ jsx(ConversationContent, { className: "tw:flex tw:items-center tw:justify-center", children: conversationContent }),
              /* @__PURE__ */ jsx(ConversationScrollButton, {})
            ] })
          }
        ),
        (!showWelcome || !hasImageCarousel) && /* @__PURE__ */ jsx(
          ConciergeInputArea,
          {
            searchInputValue,
            setSearchInputValue,
            handleSearch,
            isActivelyStreaming,
            conversationHistoryLength: conversationHistory.length,
            onTrackAccessPoint
          }
        )
      ]
    }
  );
};
const ConciergeChatAreaV2 = memo(
  ConciergeChatAreaV2Component
  // (prevProps, nextProps) => {
  //   // Skip re-render if only searchInputValue changed
  //   return (
  //     prevProps.conversationHistory === nextProps.conversationHistory &&
  //     prevProps.isActivelyStreaming === nextProps.isActivelyStreaming &&
  //     prevProps.streamingAIResponse === nextProps.streamingAIResponse &&
  //     prevProps.isLoadingConversationQuery ===
  //       nextProps.isLoadingConversationQuery &&
  //     prevProps.followUpQuestions === nextProps.followUpQuestions &&
  //     prevProps.previewSearchQueries === nextProps.previewSearchQueries &&
  //     prevProps.previewQueryProducts === nextProps.previewQueryProducts &&
  //     prevProps.streamingFeaturedProduct ===
  //       nextProps.streamingFeaturedProduct &&
  //     prevProps.productMetadataMap === nextProps.productMetadataMap &&
  //     prevProps.streamingProductGroups === nextProps.streamingProductGroups &&
  //     prevProps.currentStatus === nextProps.currentStatus &&
  //     prevProps.isWaitingForFollowUpQueries ===
  //       nextProps.isWaitingForFollowUpQueries &&
  //     prevProps.conversationId === nextProps.conversationId &&
  //     prevProps.brandId === nextProps.brandId &&
  //     prevProps.isEmbedded === nextProps.isEmbedded &&
  //     prevProps.appName === nextProps.appName &&
  //     prevProps.handleSearch === nextProps.handleSearch &&
  //     prevProps.onRetry === nextProps.onRetry &&
  //     prevProps.onUpdateFeedback === nextProps.onUpdateFeedback &&
  //     prevProps.setSearchInputValue === nextProps.setSearchInputValue
  //     // Intentionally NOT comparing searchInputValue - input is in separate component
  //   );
  // }
);
export {
  ConciergeChatAreaV2
};
//# sourceMappingURL=ConciergeChatAreaV2.js.map
